<script setup>
import {computed, onMounted, reactive, ref, watch} from "vue";

defineOptions({name: 'TicketsOpenView'})

import {useTicketStore} from "../../store/tickets.js";
import {useAuthStore} from "../../store/auth.js";
import EmptyState from "../../components/global/EmptyState.vue";
import SectionHeader from "../../components/section/SectionHeader.vue";
import Pagination from "../../components/section/Pagination.vue";
import TicketRowItem from "../../components/tickets/TicketRowItem.vue";
import {useModalStore} from "../../store/modal.js";

const modalStore = useModalStore()
const store = useTicketStore()
const authStore = useAuthStore()

const scrollSection = ref()
const isAdmin = computed(() => [
    'reseller-super-admin',
    'reseller-team-leader',
    'super-admin',
    'internal-super-admin',
    'internal-team-leader'
].includes(authStore.user.roles[0].name))

const MIN_SEARCH_LENGTH = 3

let searchDebounce

const search = ref('')

const activeSearch = computed(() => {
    const query = search.value?.trim() ?? ''

    return query.length >= MIN_SEARCH_LENGTH ? query : null
})
const selectedTickets = ref([])

const filter_data = ref()

const ticketParams = reactive({
    sort_direction: 'desc',
    assignee_id: isAdmin.value ? null : authStore.user.id,
    status: null,
    assignment: null,
    linkage: null,
    limit: 25,
    start_date: null,
    end_date: null,
})

const hasActiveFilters = computed(() =>
    !!(ticketParams.status
        || ticketParams.assignment
        || ticketParams.linkage
        || ticketParams.start_date
        || ticketParams.end_date
        || (isAdmin.value && ticketParams.assignee_id))
)

const selectAll = computed({
    get: () =>
        store.tickets?.data.length > 0 &&
        selectedTickets.value.length === store.tickets.data.length,

    set: (value) => {
        if (value) {
            selectedTickets.value = store.tickets.data.map(ticket => ticket.id)
        } else {
            selectedTickets.value = []
        }
    }
})

const handleSelectTicket = (ticketId) => {
    if (!selectedTickets.value.includes(ticketId)) {
        selectedTickets.value.push(ticketId)
    }
}

const handleDeselectTicket = (ticketId) => {
    selectedTickets.value = selectedTickets.value.filter(
        id => id !== ticketId
    )
}

const clearSelection = () => {
    selectedTickets.value = []
}

const openFilters = () => {
    modalStore.open(
        'TicketFilters',
        {
            params: ticketParams,
            isAdmin: isAdmin.value,
            onApply: () => getTickets(1),
            onClear: clearOtherFilters
        },
        'body',
        'bottom-right'
    )
}

const clearDateFilters = () => {
    ticketParams.start_date = null
    ticketParams.end_date = null
}

const clearOtherFilters = () => {
    ticketParams.sort_direction = 'desc'
    ticketParams.assignee_id = isAdmin.value ? null : authStore.user.id
    ticketParams.status = null
    ticketParams.assignment = null
    ticketParams.linkage = null
    ticketParams.start_date = null
    ticketParams.end_date = null
}

watch(search, (value, oldValue) => {
    clearTimeout(searchDebounce)

    const query = value?.trim() ?? ''
    const previousQuery = oldValue?.trim() ?? ''

    if (query.length < MIN_SEARCH_LENGTH && previousQuery.length < MIN_SEARCH_LENGTH) {
        return
    }

    searchDebounce = setTimeout(() => getTickets(1), 250)
})

const getTickets = async (page = 1) => {
    await store.getTickets({
        ...ticketParams,
        search: activeSearch.value,
        page: page
    })

    await store.getCounts()

    if (scrollSection.value) {
        scrollSection.value.scrollTop = 0
    }
}

watch(() => store.tickets, clearSelection)

onMounted(async () => {
    await getTickets()

    await store.prepareFilterData()
    filter_data.value = store.filter_data
})
</script>

<template>
    <main v-if="!store.tickets">
        <empty-state
            icon="speechbubble-double"
            heading="Loading tickets."
            content="Give it sec, it's coming in a mo."
        />
    </main>

    <main v-else-if="store.tickets.data.length || search || hasActiveFilters">
        <section>
            <section-header :heading="isAdmin ? 'All open tickets.' : 'Your open tickets.'">
                <div class="search-bar">
                    <brand-form-input
                        label="Search by Kayako number, order UUID or Afrihost ref"
                        variant="search"
                        v-model="search"
                    />

                    <button
                        class="mobile-filter-button"
                        @click.prevent="openFilters"
                    >
                        <ah-icon name="filter-default"/>
                    </button>
                </div>
            </section-header>

            <div class="ticket-counts">
                <div class="ticket-count">
                    <span>Unassigned</span>
                    <strong>{{ store.counts?.unassigned ?? 0 }}</strong>
                </div>

                <div class="ticket-count">
                    <span>Assigned</span>
                    <strong>{{ store.counts?.assigned ?? 0 }}</strong>
                </div>

                <div class="ticket-count">
                    <span>Awaiting external</span>
                    <strong>{{ store.counts?.awaiting_external ?? 0 }}</strong>
                </div>
            </div>

            <div
                class="bulk-actions-toolbar"
                v-if="selectedTickets.length > 0"
            >
                <div class="bulk-actions-info">
                    <span>
                        {{ selectedTickets.length }} ticket(s) selected
                    </span>
                </div>

                <div class="bulk-actions-buttons">
                    <brand-button
                        variant="primary"
                        @click.prevent="modalStore.open(
                            'BulkAssignTicket',
                            {ticket_ids: selectedTickets},
                            'body',
                            'bottom-right'
                        )"
                    >
                        Assign
                    </brand-button>

                    <brand-button
                        variant="primary"
                        @click.prevent="modalStore.open(
                            'BulkCompleteTicket',
                            {ticket_ids: selectedTickets},
                            'body',
                            'bottom-right'
                        )"
                    >
                        Complete
                    </brand-button>

                    <brand-button
                        variant="secondary"
                        @click.prevent="clearSelection"
                    >
                        Clear selection
                    </brand-button>
                </div>
            </div>

            <div
                class="section-table-header"
                v-if="store.tickets.data.length"
            >
                <div class="select-all-checkbox">
                    <input
                        type="checkbox"
                        :checked="selectAll"
                        @change="selectAll = $event.target.checked"
                    />

                    <label>Select all</label>
                </div>
            </div>

            <div
                class="section-table"
                ref="scrollSection"
                v-if="store.tickets.data.length"
            >
                <ticket-row-item
                    :ticket="ticket"
                    :selected="selectedTickets.includes(ticket.id)"
                    :showCheckbox="true"
                    @select="handleSelectTicket"
                    @deselect="handleDeselectTicket"
                    v-for="ticket in store.tickets.data"
                    :key="ticket.id"
                />
            </div>

            <div
                class="section-table-empty"
                v-if="!store.tickets.data.length"
            >
                <h1>No tickets match</h1>
            </div>

            <div
                class="section-footer"
                v-if="store.tickets.data.length"
            >
                <pagination
                    :pagination="store.tickets"
                    @change-page="getTickets"
                />
            </div>
        </section>

        <aside class="section-sidebar">
            <div class="sidebar-header">
                <h2>Filters</h2>

                <p @click.prevent="clearOtherFilters">
                    Clear
                </p>
            </div>

            <div class="sidebar-select">
                <brand-form-select
                    variant="sidebar"
                    label="Sorting."
                    :options="filter_data?.sorting ?? []"
                    v-model="ticketParams.sort_direction"
                />
            </div>

            <div
                class="sidebar-select"
                v-if="isAdmin"
            >
                <brand-form-select
                    variant="sidebar"
                    label="Assignee."
                    :options="filter_data?.assignees ?? []"
                    v-model="ticketParams.assignee_id"
                />
            </div>

            <div class="sidebar-select">
                <brand-form-select
                    variant="sidebar"
                    label="Status."
                    :options="filter_data?.statuses ?? []"
                    v-model="ticketParams.status"
                />
            </div>

            <div class="sidebar-select">
                <brand-form-select
                    variant="sidebar"
                    label="Assignment."
                    :options="filter_data?.assignment ?? []"
                    v-model="ticketParams.assignment"
                />
            </div>

            <div class="sidebar-select">
                <brand-form-select
                    variant="sidebar"
                    label="Linked."
                    :options="filter_data?.linkage ?? []"
                    v-model="ticketParams.linkage"
                />
            </div>

            <div class="sidebar-header">
                <h2>Date</h2>

                <p @click.prevent="clearDateFilters">
                    Clear
                </p>
            </div>

            <brand-form-input
                label="Start date."
                variant="date"
                v-model="ticketParams.start_date"
            />

            <brand-form-input
                label="End date."
                variant="date"
                v-model="ticketParams.end_date"
            />

            <div class="sidebar-footer">
                <brand-button
                    as="button"
                    variant="primary"
                    @click.prevent="getTickets(1)"
                >
                    Apply
                </brand-button>

                <brand-button
                    as="button"
                    variant="small"
                >
                    <ah-icon name="arrow-in"/>
                </brand-button>
            </div>
        </aside>
    </main>

    <main v-else>
        <empty-state
            icon="speechbubble-double"
            heading="No pending tickets."
            content="Once a ticket is escalated it will show up here."
        />
    </main>
</template>

<style scoped lang="scss">
.search-bar {
    min-width: 34rem;
}

.ticket-counts {
    display: flex;
    gap: 1rem;
    margin: 2rem 0;
}

.ticket-count {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    background: var(--color-white);
    border-radius: 2.4rem;

    span {
        @include font(1.4, 1.8, black);
    }

    strong {
        @include font(2, 2.4, black);
    }

    @include dark() {
        background: var(--color-darth-maul);

        span,
        strong {
            color: var(--color-white);
        }
    }
}

.bulk-actions {
    &-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.5rem 2rem;
        background: var(--color-white);
        border-radius: 2.4rem;
        margin: 2rem 0;
        gap: 2rem;
    }

    &-info {
        @include font(1.4, 1.8, black);
    }

    &-buttons {
        display: flex;
        gap: 1rem;
    }

    @include dark() {
        &-toolbar {
            background: var(--color-darth-maul);
        }

        &-info {
            color: var(--color-white);
        }
    }
}

.section-table-header {
    padding: 1rem 2rem;
    background: var(--color-grey-mist);
    border-radius: 2.4rem 2.4rem 0 0;

    .select-all-checkbox {
        display: flex;
        align-items: center;
        gap: 1rem;

        input[type="checkbox"] {
            width: 2rem;
            height: 2rem;
            cursor: pointer;
        }

        label {
            @include font(1.4, 1.8, black);
            cursor: pointer;
        }
    }

    @include dark() {
        background: var(--color-darth-vader);
    }
}

section {
    display: flex;
    flex-direction: column;

    .section-header {
        flex-shrink: 0;
    }

    .ticket-counts {
        flex-shrink: 0;
    }

    .bulk-actions-toolbar {
        flex-shrink: 0;
    }

    .section-table-header {
        flex-shrink: 0;
    }

    .section-table {
        flex: 1;
        min-height: 0;
        margin-top: 2rem;
        height: auto !important;
    }

    .section-footer {
        flex-shrink: 0;
    }
}
</style>
